import React from 'react';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { BrowserRouter, Route, Routes, useSearchParams } from 'react-router-dom';
import QuestionCard from '@/components/staffer/Grading/QuestionCards/QuestionCard';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { applyGradeByQuestionFilters } from '@/components/staffer/Grading/utils';
import BreadcrumbsNavigator, { BreadcrumbsNavigatorProps } from '@/components/shared/Breadcrumbs/BreadcrumbsNavigator';
import QuestionGradingHooks from './QuestionGradingHooks';
import HeaderWithFilters from './HeaderWithFilters';
import StudentAnswerCard from './StudentAnswerCard';

interface QuestionGradingProps {
  breadcrumbsData: BreadcrumbsNavigatorProps
  programId: number;
  userId: number;
  userType: string;
}

const QuestionGrading = ({ breadcrumbsData, programId, userId, userType } : QuestionGradingProps) => {
  QuestionGradingHooks({ programId, userId, userType });
  const { isLoading, assignment, classroom, questionAnswers, questionId } = useGradingStore();
  const [searchParams] = useSearchParams();

  const renderBreadcrumbsNav = () => (
    <BreadcrumbsNavigator
      page="grades"
      searchParams={searchParams}
      {...breadcrumbsData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderBreadcrumbsNav()}
        <span>Loading...</span>
      </>
    );
  }

  const question = assignment.questions.find(q => q.id === questionId);
  const { students } = classroom;
  const questionAnswersForCurrentQuestion = (questionAnswers || []).filter(qa => qa.question_id === questionId);
  const { filters } = useGradingStore.getState();
  const { questionsFilter, statusFilter } = filters;

  // For QA purposes, until question answer cards are implemented
  const renderPlaceholderData = () => {
    const filteredStudents = applyGradeByQuestionFilters({
      questionAnswers: questionAnswersForCurrentQuestion,
      questionsFilter,
      statusFilter,
      students,
    });
    return (
      <>
        <QuestionCard question={question} />
        <hr className="tw-mx-[-20px] tw-text-med-grey" />
        <div className="tw-font-bold tw-text-lg tw-pb-4">
          Student Results
        </div>
        {filteredStudents.map((student) => {
          const questionAnswer = questionAnswersForCurrentQuestion.find(qa => qa.student_id === student.id);

          return (
            <div key={`${student.id}-${question.id}`}>
              <StudentAnswerCard
                question={question}
                questionAnswer={questionAnswer}
                student={student}
              />
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      {renderBreadcrumbsNav()}
      <div className="tw-py-4">
        <h1>{assignment.assignment_details.type} Results by Question</h1>
      </div>
      <HeaderWithFilters />
      {renderPlaceholderData()}
    </>
  );
};

const QuestionGradingWrapper = ({ breadcrumbsData, programId, userId, userType } : QuestionGradingProps) => (
  <ReactQueryProvider>
    <BrowserRouter basename="/teacher/assignments">
      <Routes>
        <Route
          element={(
            <QuestionGrading
              breadcrumbsData={breadcrumbsData}
              programId={programId}
              userId={userId}
              userType={userType}
            />
          )}
          path=":assignmentId/questions/:questionId"
        />
      </Routes>
    </BrowserRouter>
  </ReactQueryProvider>
);

export default QuestionGradingWrapper;
